import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import qs from 'query-string';

import FullScreenLoader from '../../../global/FullScreenLoader';
import ErrorView from '../../../global/ErrorView';
import { getBasenameFromEntity } from '../../../../util/role-definitions';
import { MeWithAlbumMembershipByAlbumId } from '../../../../graphql/queries/account';
import { isProAccount } from '../../../../util/account';
import { BASENAME } from '../../../../constants/basenames';
import { withContributorPermissions } from '../../../../apps/pro/services/contributors';
import { CONTRIBUTOR_VIEW_ACCESS } from '../../../../constants/contributors';
import { isAlbum } from '../../../../util/album';
import { getVideoEntityFromLinks } from './helpers';

const propTypes = {
  loading: PropTypes.bool,
  video: PropTypes.shape({
    id: PropTypes.string,
  }),
  photo: PropTypes.shape({
    id: PropTypes.string,
    is_owner: PropTypes.string,
    is_uploader: PropTypes.string,
    albums: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
  }),

  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembership: PropTypes.shape({
    id: PropTypes.string,
  }),
  isContributor: PropTypes.bool,
  loadingPermissions: PropTypes.bool,
  searchQuery: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};

const AccountFilesRedirect = ({
  history,
  loading,
  user,
  photo,
  video,
  albumMembership,
  isContributor,
  loadingPermissions,
  searchQuery,
}) => {
  const onRedirect = async () => {
    if (photo) {
      const [album] = photo.albums?.records;
      const basename =
        isProAccount(user) && !isContributor
          ? getBasenameFromEntity(album)
          : BASENAME.SHUTTERBUG;

      if (album?.creator?.id === user?.id || isContributor) {
        // This qualifies for both owners and contributors
        history.replace({
          pathname: `/app/${basename}/albums/${album.id}/photos/v/${photo.id}`,
          search: qs.stringify(searchQuery),
        });
      } else if (albumMembership) {
        // Force SB basename since it's the one memberships are handled in.
        history.replace({
          pathname: albumMembership?.has_all_photos_access
            ? `/app/shutterbug/hierarchy-items/${album?.id}/v/${photo.id}`
            : // This one misses the photo ID because we'd require this to be a matchedPhotoId. best we can do
              // Is land the user among their matches, but no comment/reaction redirection possible here.
              `/app/shutterbug/hierarchy-items/${albumMembership?.root_album_membership_group?.id}/matches`,
          search: qs.stringify(searchQuery),
        });
      }
    }

    if (video) {
      const entity = getVideoEntityFromLinks(video);

      if (!entity) {
        return;
      }

      const basename = isContributor
        ? BASENAME.SHUTTERBUG
        : getBasenameFromEntity(entity);

      history.replace({
        pathname: (() => {
          if (isAlbum(entity)) {
            return entity?.is_video_album
              ? // covers VGB case
                `/app/${basename}/albums/${entity?.id}/photos/v/${video.id}`
              : `/app/${basename}/albums/${entity?.id}/videos/v/${video.id}`;
          }

          return `/app/${basename}/folders/${entity?.id}/videos/v/${video.id}`;
        })(),
        search: qs.stringify(searchQuery),
      });
    }
  };
  useEffect(() => {
    if ((photo || video) && !loading && !loadingPermissions) {
      onRedirect();
    }
  }, [photo, video, loading, loadingPermissions]);

  if (!loading && !photo && !video && !loadingPermissions) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'Sorry, it looks like we cannot process your request at this time',
          },
        }}
      />
    );
  }
  return <FullScreenLoader />;
};

AccountFilesRedirect.propTypes = propTypes;

const withMeWithAlbumMembershipByAlbumId = graphql(
  MeWithAlbumMembershipByAlbumId,
  {
    props: ({ data: { me, loading } = {}, ownProps }) => ({
      user: me,
      albumMembership: me?.album_memberships?.records?.[0],
      loading: ownProps.loading || loading,
    }),
    options: ({ photo }) => {
      const [album] = photo?.albums?.records ?? [];

      return {
        variables: {
          id: album?.id,
        },
        fetchPolicy: 'cache-and-network',
      };
    },
    skip: ({ photo }) =>
      //  We don't care about this for video
      !photo,
  },
);

export default compose(
  withRouter,
  withMeWithAlbumMembershipByAlbumId,
  withContributorPermissions(({ photo, video }) => {
    const [album] = photo?.albums?.records ?? [];
    const entity = getVideoEntityFromLinks(video);

    return {
      entityId: photo ? album?.id : entity?.id,
      permissions: [CONTRIBUTOR_VIEW_ACCESS.ALL],
    };
  }),
)(AccountFilesRedirect);
